import { useEffect, useState } from 'react';

import { resolveFlowEligibility } from 'pages/MinhaEquipe/ApreciarRealizacao/shared/eligibility';
import { UserInfoInterface } from 'types/user';

import { isAppreciationLadderManager } from './taskCyclesVisibility';

interface UsePendingAppreciationParams {
  // Chave do player (`F<matrícula>`) — é o que o aggregate espera, não a matrícula crua.
  playerId?: string;
  userInfo?: UserInfoInterface;
  // Só o ciclo corrente: o aggregate resolve sempre o ciclo `open`, então num ciclo
  // passado a contagem não descreveria a tela.
  currentCycle: boolean;
}

// Realizações aguardando apreciação DESTE gestor agora, somando os dois fluxos
// (Normal + MAD) — a home é o guarda-chuva da apreciação, não uma aba.
// `null` = ainda não resolvido ou aggregate indisponível; o chamador cai no fallback local.
//
// A consulta é exclusiva de gestor da escada de apreciação: quem não aprecia nunca chama.
//
// REVALIDAÇÃO: a carga inicial aceita o cache de sessão (TTL 5 min) — é ele que faz
// home → apreciar → home custar uma requisição só. Mas a fila é COMPARTILHADA com os
// parceiros de dupla (qualquer um do par aprecia), então o número envelhece por ação de
// terceiros, não só da própria pessoa. Por isso revalidamos com `fresh` quando a aba volta
// ao foco: cobre "apreciei em outra aba/pelo parceiro e voltei" sem criar polling.
export function usePendingAppreciation({ playerId, userInfo, currentCycle }: UsePendingAppreciationParams): number | null {
  const [pending, setPending] = useState<number | null>(null);

  const active = !!playerId && currentCycle && isAppreciationLadderManager(userInfo);

  useEffect(() => {
    // Sai da escada / troca para ciclo passado: descarta a contagem para não deixar um
    // número velho decidindo a mensagem.
    if (!active) {
      setPending(null);
      return;
    }

    let cancelled = false;

    const load = (fresh: boolean) => {
      resolveFlowEligibility(playerId, { fresh }).then(eligibility => {
        // `null` é falha de rede/servidor — preserva o último valor bom em vez de zerar,
        // que exibiria "não há nada aguardando" por causa de um erro transitório.
        if (cancelled || !eligibility) return;

        setPending((eligibility.normalPending ?? 0) + (eligibility.madPending ?? 0));
      });
    };

    load(false);

    const revalidateOnFocus = () => {
      if (document.visibilityState === 'visible') {
        load(true);
      }
    };

    document.addEventListener('visibilitychange', revalidateOnFocus);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', revalidateOnFocus);
    };
  }, [active, playerId]);

  return pending;
}
